import React from "react";
import {
    Activity,
    ShieldPlus,
    Waves,
    Moon,
    Bone,
    TrendingUp,
} from "lucide-react";
import {
    LOGO_URL,
    PHONE_DISPLAY,
    EMAIL,
    INSTAGRAM,
    waUrl,
} from "../lib/constants";

/**
 * 2-page A4 ATHLETE brochure for The Reset Sanctuary.
 * Targeting professional athletes & competitive sports performers.
 * Page 1: Problem · Solution · 6 Benefits
 * Page 2: Science · Equipment · Tatva K · Packages · Contact
 * View at /brochure-athletes. Print → Save as PDF.
 * Compliance verified — only "therapy" allowed in Berklee credential.
 */

const GOLD = "#c9a227";
const GOLD_LINE = "rgba(201,162,39,0.25)";
const GOLD_LINE_SOFT = "rgba(201,162,39,0.15)";

const PageFrame = ({ children, testid }) => (
    <article
        className="brochure-page brochure-ath-page"
        data-testid={testid}
        style={{
            width: 794,
            minHeight: 1123,
            maxWidth: "100%",
            margin: "0 auto 32px",
            backgroundColor: "#0a0a0a",
            color: "#bbb",
            boxShadow:
                "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.18)",
            padding: "40px 44px 32px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: "Inter, sans-serif",
            display: "flex",
            flexDirection: "column",
        }}
    >
        <div
            style={{
                position: "absolute",
                inset: 18,
                border: "1px solid rgba(201,162,39,0.22)",
                pointerEvents: "none",
            }}
        />
        {children}
    </article>
);

const PageHeader = ({ kicker }) => (
    <header
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            marginBottom: 14,
        }}
    >
        <img src={LOGO_URL} alt="The Reset Sanctuary" style={{ height: 58 }} />
        <div style={{ textAlign: "right" }}>
            <p
                style={{
                    color: GOLD,
                    fontSize: 9,
                    letterSpacing: "0.35em",
                    textTransform: "uppercase",
                    margin: 0,
                    fontWeight: 500,
                }}
            >
                {kicker}
            </p>
            <p
                style={{
                    color: "#888",
                    fontSize: 8.5,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    margin: "4px 0 0",
                }}
            >
                Dubai · RAK
            </p>
        </div>
    </header>
);

const PageFooter = ({ pageNum, tagline }) => (
    <div
        style={{
            marginTop: "auto",
            paddingTop: 14,
            borderTop: `1px solid ${GOLD_LINE_SOFT}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            fontSize: 8,
            color: "#666",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            gap: 12,
        }}
    >
        <span>theresetsanctuary.org</span>
        <span style={{ color: GOLD, textAlign: "center", flex: 1 }}>
            {tagline}
        </span>
        <span>Page {pageNum} / 2</span>
    </div>
);

const SectionTitle = ({ kicker, title, color = "#fff" }) => (
    <div style={{ marginBottom: 10 }}>
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
            }}
        >
            {kicker}
        </p>
        <h2
            className="font-serif"
            style={{
                color,
                fontSize: 20,
                fontWeight: 500,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.005em",
            }}
        >
            {title}
        </h2>
    </div>
);

const BenefitTile = ({ Icon, label, body, testid }) => (
    <div
        data-testid={testid}
        style={{
            padding: "14px 13px",
            border: `1px solid ${GOLD_LINE}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            backgroundColor: "rgba(201,162,39,0.025)",
        }}
    >
        <Icon size={24} color={GOLD} strokeWidth={1.4} />
        <p
            style={{
                color: "#fff",
                fontSize: 10,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
                lineHeight: 1.25,
            }}
        >
            {label}
        </p>
        <p
            style={{
                color: "#aaa",
                fontSize: 8.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const Pillar = ({ title, body }) => (
    <div style={{ padding: "12px 12px", borderTop: `1px solid ${GOLD_LINE}` }}>
        <p
            style={{
                color: GOLD,
                fontSize: 8.5,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
                lineHeight: 1.3,
            }}
        >
            {title}
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9,
                lineHeight: 1.55,
                margin: 0,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const EquipmentCard = ({ src, label, body, testid, fallbackIcon }) => (
    <div
        data-testid={testid}
        style={{
            border: `1px solid ${GOLD_LINE}`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "rgba(201,162,39,0.025)",
        }}
    >
        <div
            style={{
                width: "100%",
                aspectRatio: "16 / 10",
                overflow: "hidden",
                backgroundColor: "#0a0a0a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {src ? (
                <img
                    src={src}
                    alt={label}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            ) : (
                fallbackIcon
            )}
        </div>
        <div style={{ padding: "8px 12px 10px" }}>
            <p
                style={{
                    color: GOLD,
                    fontSize: 8.5,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    margin: "0 0 3px",
                    fontWeight: 500,
                }}
            >
                {label}
            </p>
            <p
                style={{
                    color: "#bbb",
                    fontSize: 8.5,
                    lineHeight: 1.45,
                    margin: 0,
                    fontWeight: 300,
                }}
            >
                {body}
            </p>
        </div>
    </div>
);

// Minimal gold-line glyph for equipment cards without product photos
const Glyph = ({ children }) => (
    <svg
        width="60"
        height="42"
        viewBox="0 0 60 42"
        xmlns="http://www.w3.org/2000/svg"
        style={{ opacity: 0.85 }}
    >
        {children}
    </svg>
);

const Package = ({ tier, price, period, headline, items, featured }) => (
    <div
        style={{
            padding: "18px 16px 20px",
            border: featured ? `1px solid ${GOLD}` : `1px solid ${GOLD_LINE}`,
            backgroundColor: featured ? "rgba(201,162,39,0.04)" : "transparent",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            height: "100%",
        }}
        data-testid={`ath-package-${tier.toLowerCase()}`}
    >
        {featured && (
            <span
                style={{
                    position: "absolute",
                    top: -10,
                    left: 14,
                    backgroundColor: GOLD,
                    color: "#0a0a0a",
                    fontSize: 8,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    padding: "3px 10px",
                    fontWeight: 600,
                }}
            >
                Most Chosen
            </span>
        )}
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
            }}
        >
            {tier}
        </p>
        <p
            className="font-serif"
            style={{
                color: "#fff",
                fontSize: 22,
                fontWeight: 400,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
            }}
        >
            AED {price}
            <span
                style={{
                    fontSize: 11,
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    letterSpacing: "0.05em",
                    marginLeft: 4,
                }}
            >
                /{period}
            </span>
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {headline}
        </p>
        <div
            style={{
                borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                marginTop: 2,
                paddingTop: 8,
            }}
        >
            {items.map((it, i) => (
                <p
                    key={i}
                    style={{
                        color: "#fff",
                        fontSize: 9.5,
                        margin: "3px 0",
                        lineHeight: 1.4,
                    }}
                >
                    · {it}
                </p>
            ))}
        </div>
    </div>
);

const BrochureAthletes = () => {
    const athWa = waUrl(
        "Hi, I'd like to book an athlete recovery session at The Reset Sanctuary.",
    );

    return (
        <main
            data-testid="brochure-athletes-page"
            style={{
                backgroundColor: "#0a0a0a",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                color: "#bbb",
            }}
        >
            {/* Screen-only print button */}
            <div
                className="brochure-screen-only"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <p
                    style={{
                        color: "#888",
                        fontSize: 12,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    Athlete Brochure — A4 · 2 pages
                </p>
                <button
                    onClick={() => window.print()}
                    className="btn-gold-solid"
                    data-testid="brochure-ath-print-btn"
                    style={{
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                    }}
                >
                    Save as PDF / Print
                </button>
            </div>

            {/* ============== PAGE 1 ============== */}
            <PageFrame testid="brochure-ath-a4">
                <PageHeader kicker="Sound Recovery for Athletes" />

                {/* HERO */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                >
                    <p
                        style={{
                            color: GOLD,
                            fontSize: 9,
                            letterSpacing: "0.35em",
                            textTransform: "uppercase",
                            margin: "0 0 8px",
                            fontWeight: 500,
                        }}
                    >
                        Sound Recovery for Sports Performance
                    </p>
                    <h1
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: 36,
                            fontWeight: 400,
                            lineHeight: 1.02,
                            letterSpacing: "-0.025em",
                            margin: "0 0 8px",
                            textTransform: "uppercase",
                        }}
                    >
                        Recover Faster.{" "}
                        <span style={{ color: GOLD }}>Perform Longer.</span>
                    </h1>
                </section>

                {/* HERO IMAGE — male athlete */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        width: "100%",
                        aspectRatio: "16 / 7.5",
                        overflow: "hidden",
                        border: `1px solid ${GOLD_LINE}`,
                    }}
                    data-testid="ath-hero-image"
                >
                    <img
                        src="/brochure-assets/athlete-male.jpg"
                        alt="Professional athlete in recovery"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center 30%",
                            display: "block",
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.7) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                </section>

                {/* HERO SUBTEXT */}
                <p
                    style={{
                        position: "relative",
                        zIndex: 1,
                        color: "#bbb",
                        fontSize: 10.5,
                        lineHeight: 1.6,
                        margin: "0 0 14px",
                        maxWidth: 660,
                        fontWeight: 300,
                    }}
                    data-testid="ath-hero-subtext"
                >
                    Evidence-informed sound and frequency work designed to
                    accelerate muscle recovery, reduce inflammation, and extend
                    athletic performance across professional athletes,
                    competitive sports performers, and training teams.
                </p>

                {/* PROBLEM + SOLUTION */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                    }}
                >
                    <div data-testid="ath-problem">
                        <SectionTitle
                            kicker="The Problem"
                            title="Training breaks the body. Recovery rebuilds it."
                        />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            You push limits daily. But your recovery protocol
                            isn&apos;t keeping pace. Muscle soreness, delayed
                            tissue repair, chronic inflammation and stalled
                            performance gains aren&apos;t a training problem —
                            they are a recovery deficit. Conventional recovery
                            rarely reaches the nervous system at the depth
                            required to truly reset.
                        </p>
                    </div>
                    <div data-testid="ath-solution">
                        <SectionTitle
                            kicker="The Solution"
                            title="Precision Frequency Recovery."
                            color={GOLD}
                        />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            45 minutes. Sound, Hz, vibration. A protocol
                            engineered to downshift the nervous system,
                            accelerate tissue repair, reduce inflammation and
                            unlock the deep recovery your training demands.
                        </p>
                    </div>
                </section>

                {/* 6 BENEFITS */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 12,
                    }}
                    data-testid="ath-benefits"
                >
                    <SectionTitle
                        kicker="The Benefits"
                        title="Six outcomes. One session."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 10,
                        }}
                    >
                        <BenefitTile
                            Icon={Activity}
                            label="Faster Muscle Recovery"
                            body="Frequency-based sound promotes circulation, reduces inflammation and speeds up muscle recovery time."
                            testid="ath-benefit-recovery"
                        />
                        <BenefitTile
                            Icon={ShieldPlus}
                            label="Supports Injury Healing"
                            body="Helps accelerate tissue repair, reduces pain and supports long-term injury rehabilitation."
                            testid="ath-benefit-injury"
                        />
                        <BenefitTile
                            Icon={Waves}
                            label="Reduces Stress & Tension"
                            body="Lowers cortisol, relaxes the nervous system — a mental and physical reset."
                            testid="ath-benefit-stress"
                        />
                        <BenefitTile
                            Icon={Moon}
                            label="Improves Sleep Quality"
                            body="Deeper relaxation leads to better sleep, hormonal balance and overall recovery."
                            testid="ath-benefit-sleep"
                        />
                        <BenefitTile
                            Icon={Bone}
                            label="Relieves Joint & Tissue Tension"
                            body="Frequency sound waves help release tightness in joints, fascia and connective tissues."
                            testid="ath-benefit-joint"
                        />
                        <BenefitTile
                            Icon={TrendingUp}
                            label="Boosts Performance & Consistency"
                            body="Recover better, train harder, stay consistent with more energy, focus and resilience."
                            testid="ath-benefit-perf"
                        />
                    </div>
                </section>

                <PageFooter
                    pageNum={1}
                    tagline="Train Hard · Recover Deep · Become Unstoppable"
                />
            </PageFrame>

            {/* ============== PAGE 2 ============== */}
            <PageFrame testid="brochure-ath-a4-page2">
                <PageHeader kicker="Science · Equipment · Packages" />

                {/* SCIENCE — 4 pillars */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                    data-testid="ath-science"
                >
                    <SectionTitle
                        kicker="The Science"
                        title="How the protocol works."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "2px 16px",
                        }}
                    >
                        <Pillar
                            title="Theta Brainwaves"
                            body="The state where muscle repair accelerates and mental clarity returns."
                        />
                        <Pillar
                            title="Schumann Resonance · 7.83 Hz"
                            body="Earth's natural frequency — cellular recovery and nervous system regulation."
                        />
                        <Pillar
                            title="Binaural Beats"
                            body="Brainwave entrainment for deep relaxation, cognitive clarity and mental resilience."
                        />
                        <Pillar
                            title="Vibroacoustic Technology"
                            body="Low-frequency mechanical vibration reduces stress and supports nervous system regulation."
                        />
                    </div>
                </section>

                {/* EQUIPMENT — 6 cards, 2 with photos + 4 with glyphs */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                    data-testid="ath-equipment"
                >
                    <SectionTitle
                        kicker="Equipment"
                        title="Engineered for measurable reset."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 10,
                        }}
                    >
                        <EquipmentCard
                            src="/brochure-assets/pemf-mat.jpg"
                            label="PEMF Mat"
                            body="Pulsed electromagnetic stimulation for cellular recovery and inflammation reduction."
                            testid="ath-eq-pemf"
                        />
                        <EquipmentCard
                            label="Frequency Generator"
                            body="Precision frequencies that restore balance and optimise vitality."
                            testid="ath-eq-generator"
                            fallbackIcon={
                                <Glyph>
                                    <rect
                                        x="6"
                                        y="14"
                                        width="48"
                                        height="14"
                                        rx="2"
                                        stroke={GOLD}
                                        strokeWidth="1"
                                        fill="none"
                                    />
                                    <circle
                                        cx="14"
                                        cy="21"
                                        r="3"
                                        stroke={GOLD}
                                        strokeWidth="1"
                                        fill="none"
                                    />
                                    <path
                                        d="M22 21 H50"
                                        stroke={GOLD}
                                        strokeWidth="0.8"
                                        opacity="0.7"
                                    />
                                    <path
                                        d="M22 17 H42"
                                        stroke={GOLD}
                                        strokeWidth="0.8"
                                        opacity="0.5"
                                    />
                                    <path
                                        d="M22 25 H46"
                                        stroke={GOLD}
                                        strokeWidth="0.8"
                                        opacity="0.5"
                                    />
                                </Glyph>
                            }
                        />
                        <EquipmentCard
                            label="Vibroacoustic Bed"
                            body="Low-frequency mechanical vibration applied to biological tissue."
                            testid="ath-eq-vibro"
                            fallbackIcon={
                                <Glyph>
                                    <rect
                                        x="6"
                                        y="16"
                                        width="48"
                                        height="10"
                                        rx="3"
                                        stroke={GOLD}
                                        strokeWidth="1"
                                        fill="none"
                                    />
                                    <path
                                        d="M2 28 Q12 24 22 28 T42 28 T58 28"
                                        stroke={GOLD}
                                        strokeWidth="0.8"
                                        fill="none"
                                        opacity="0.7"
                                    />
                                    <path
                                        d="M2 34 Q12 30 22 34 T42 34 T58 34"
                                        stroke={GOLD}
                                        strokeWidth="0.8"
                                        fill="none"
                                        opacity="0.4"
                                    />
                                </Glyph>
                            }
                        />
                        <EquipmentCard
                            label="Binaural Beats"
                            body="Brainwave entrainment for deep relaxation."
                            testid="ath-eq-binaural"
                            fallbackIcon={
                                <Glyph>
                                    <circle
                                        cx="20"
                                        cy="21"
                                        r="10"
                                        stroke={GOLD}
                                        strokeWidth="1"
                                        fill="none"
                                    />
                                    <circle
                                        cx="40"
                                        cy="21"
                                        r="10"
                                        stroke={GOLD}
                                        strokeWidth="1"
                                        fill="none"
                                    />
                                    <path
                                        d="M30 12 V30"
                                        stroke={GOLD}
                                        strokeWidth="0.6"
                                        opacity="0.6"
                                    />
                                </Glyph>
                            }
                        />
                        <EquipmentCard
                            label="Singing Bowls"
                            body="Harmonic resonance for autonomic downshift."
                            testid="ath-eq-bowls"
                            fallbackIcon={
                                <Glyph>
                                    <path
                                        d="M14 18 L46 18 L42 32 Q30 36 18 32 Z"
                                        stroke={GOLD}
                                        strokeWidth="1"
                                        fill="none"
                                    />
                                    <ellipse
                                        cx="30"
                                        cy="18"
                                        rx="16"
                                        ry="3"
                                        stroke={GOLD}
                                        strokeWidth="0.8"
                                        fill="none"
                                        opacity="0.6"
                                    />
                                </Glyph>
                            }
                        />
                        <EquipmentCard
                            src="/brochure-assets/tuning-forks.jpg"
                            label="Tuning Forks · 432/528/174/741 Hz"
                            body="Targeted frequency points calibrated to the body's recovery bands."
                            testid="ath-eq-forks"
                        />
                    </div>
                </section>

                {/* PRACTITIONER */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        padding: "12px 14px",
                        border: `1px solid ${GOLD_LINE}`,
                        display: "grid",
                        gridTemplateColumns: "62px 1fr",
                        gap: 12,
                        alignItems: "center",
                    }}
                >
                    <img
                        src="/brochure-assets/tatva.png"
                        alt="Tatva K"
                        style={{
                            width: 62,
                            height: 62,
                            objectFit: "cover",
                            objectPosition: "center 25%",
                            borderRadius: "50%",
                            border: `1px solid rgba(201,162,39,0.4)`,
                            display: "block",
                        }}
                        data-testid="ath-tatva-photo"
                    />
                    <div>
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                margin: "0 0 4px",
                                fontWeight: 500,
                            }}
                        >
                            Led by Tatva K
                        </p>
                        <p
                            style={{
                                color: "#fff",
                                fontSize: 9,
                                lineHeight: 1.5,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            TEDx Speaker · 150M+ Streams · 182K Monthly
                            Listeners · Reiki Grandmaster · Berklee College of
                            Music — Music Therapy Masterclass · ISTA Certified
                            · IPHM Certified
                        </p>
                    </div>
                </section>

                {/* PACKAGES */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            marginBottom: 14,
                        }}
                    >
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: 0,
                                letterSpacing: "0.01em",
                            }}
                        >
                            Athlete Packages
                        </h2>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 9,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                            }}
                        >
                            Monthly · Dubai & RAK
                        </p>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 14,
                        }}
                    >
                        <Package
                            tier="Basic"
                            price="3,999"
                            period="month"
                            headline="One reset a week. Stay consistent."
                            items={[
                                "1 session per week",
                                "Group format",
                                "45-min protocol",
                                "Post-session debrief",
                            ]}
                        />
                        <Package
                            tier="Standard"
                            price="7,999"
                            period="month"
                            headline="Two resets a week. Compound the gains."
                            featured
                            items={[
                                "2 sessions per week",
                                "Group format",
                                "45-min protocol",
                                "Recovery tracking",
                                "Priority scheduling",
                            ]}
                        />
                        <Package
                            tier="Premium"
                            price="11,999"
                            period="month"
                            headline="Three resets a week + 1:1."
                            items={[
                                "3 sessions per week",
                                "Group + 1:1 mix",
                                "60-min protocol",
                                "Personal protocol design",
                                "Direct line to practitioner",
                            ]}
                        />
                    </div>
                    <p
                        style={{
                            color: "#777",
                            fontSize: 8.5,
                            margin: "10px 0 0",
                            letterSpacing: "0.04em",
                        }}
                    >
                        All prices in AED, monthly. Cancel anytime. Custom
                        sports team and franchise partnerships available.
                    </p>
                </section>

                {/* PAYMENT + CONTACT */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1fr",
                        gap: 18,
                        marginBottom: 10,
                    }}
                >
                    <div>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 20,
                                fontWeight: 400,
                                margin: "0 0 6px",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Book Your{" "}
                            <span style={{ color: GOLD }}>
                                Athlete Session.
                            </span>
                        </h2>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.55,
                                margin: 0,
                                maxWidth: 360,
                                fontWeight: 300,
                            }}
                        >
                            Reserve via WhatsApp. Same-day confirmation. Walk
                            in, drop the load, walk out reset.
                        </p>
                        <div
                            style={{
                                marginTop: 12,
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                            data-testid="ath-payments"
                        >
                            <p
                                style={{
                                    color: GOLD,
                                    fontSize: 8,
                                    letterSpacing: "0.3em",
                                    textTransform: "uppercase",
                                    margin: 0,
                                    fontWeight: 500,
                                }}
                            >
                                Flexible Payment Options
                            </p>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 10,
                                    flexWrap: "wrap",
                                }}
                            >
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="ath-tabby"
                                >
                                    <img
                                        src="/brochure-assets/tabby-logo.png"
                                        alt="tabby"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        4 interest-free payments
                                    </span>
                                </span>
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="ath-tamara"
                                >
                                    <img
                                        src="/brochure-assets/tamara-logo.png"
                                        alt="tamara"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        Split your payment
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            textAlign: "right",
                            fontFamily: "Inter, sans-serif",
                        }}
                    >
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                                fontWeight: 500,
                            }}
                        >
                            Reserve via WhatsApp
                        </p>
                        <p
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: "0 0 6px",
                                letterSpacing: "0.02em",
                            }}
                        >
                            {PHONE_DISPLAY}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            {EMAIL}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            theresetsanctuary.org
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 10px",
                            }}
                        >
                            Instagram {INSTAGRAM}
                        </p>
                        <a
                            href={athWa}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="ath-cta"
                            className="brochure-screen-only"
                            style={{
                                display: "inline-block",
                                color: "#0a0a0a",
                                backgroundColor: GOLD,
                                padding: "10px 18px",
                                fontSize: 11,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                fontWeight: 600,
                                textDecoration: "none",
                            }}
                        >
                            Reserve Athlete Session
                        </a>
                    </div>
                </section>

                {/* DISCLAIMER */}
                <p
                    style={{
                        color: "#666",
                        fontSize: 7,
                        lineHeight: 1.5,
                        textAlign: "center",
                        margin: "0 0 10px",
                        position: "relative",
                        zIndex: 1,
                        paddingTop: 8,
                        borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                    }}
                >
                    The Reset Sanctuary offers wellness and recovery sessions
                    through precision sound and frequency work. We do not
                    diagnose, treat, or cure medical conditions. Our services
                    are complementary wellness practices, not medical care.
                    Please consult a licensed healthcare provider for medical
                    concerns. © 2026 Tatva K.
                </p>

                <PageFooter
                    pageNum={2}
                    tagline="Train Hard · Recover Deep · Become Unstoppable"
                />
            </PageFrame>
        </main>
    );
};

export default BrochureAthletes;
